import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import DonutsChart from '../../components/Charts/DonutsChart.v2'
import { onGetSmallerCompaniesParticipationThunk } from './SmallerCompaniesParticipation.actions'
import DataLayout, { DataLayoutChild, DataLayoutViews, useDataLayout } from '../../components/Layouts/DataLayout'
import { useSelect } from '@chilecompra/react-kit'
import { Card, DataTable, Select, Typography } from '@chilecompra/react-kit/components'
import generateYearsBetween from '../../modules/utils/years'
import exportToImage, { exportToCsv } from '../../modules/utils/exports'
import InfoEMTDialog from '../../components/Dialogs/InfoEMTDialog'
import {
  DialogLink,
  CardCustom,
  Content,
  Description,
  ExclamationIcon,
  Info,
  InfoCircle,
  InfoLabel,
  GridContainer,
  GridItem,
  Root,
  Strong,
  Title
} from './SmallerCompaniesParticipation.styles'
import { getMonth } from '../../modules/utils/months'
import InfoSmallerCompaniesParticipation from '../../components/Infographics/InfoSmallerCompaniesParticipation'
import { PARTICIPATIONS_COLORS } from '../../config/settings/constants'

/**
 * The SmallerCompaniesParticipation's container.
 */
const SmallerCompaniesParticipation = () => {
  const dispatch = useDispatch()
  const { latestUpdate, loading, startYear, endYear } = useSelector(state => state.topNavbar)
  const { data, csv } = useSelector(state => state.smallerCompaniesParticipation)
  const [helpOpen, setHelpOpen] = useState(false)
  const [activeView, setActiveView] = useState(0)
  const handleCloseHelp = useCallback(() => setHelpOpen(false), [])
  const handleOpenHelp = useCallback(() => setHelpOpen(true), [])

  const { value: yearValue, onChange: handleChangeYear } = useSelect({
    initialValue: getMonth() === 0 ? endYear - 1 : endYear,
    overwriteCallback: () => endYear
  })
  const yearsOptions = useMemo(() => generateYearsBetween(endYear, startYear), [endYear])
  const {
    disableCaptureImage,
    disableDownloadFile,
    ref: dataLayoutRef,
    onCaptureImage: handleCaptureImage,
    onChangeView: handleChangeView,
    onDownloadFile: handleDownloadFile
  } = useDataLayout({
    dependencies: [csv],
    captureImageCallback: () =>
      exportToImage('smaller-companies-participation-chart', 'empresas-menor-tamano-participacion'),
    downloadFileCallback: () => exportToCsv(csv, 'Cifras_Mensuales_EMT')
  })

  const series = useMemo(() => {
    const dataFiltered = data.filter(item => item.year === parseInt(yearValue))

    if (!dataFiltered || dataFiltered.length === 0) return []

    const item = dataFiltered[0]

    const quantityOffers = {
      name: PARTICIPATIONS_COLORS.find(d => d.value === 0).name,
      data: item.quantityOffersEMT,
      dataTotals: item.quantityOffersTotals,
      percentage: item.percentageOffersEMT,
      color: PARTICIPATIONS_COLORS.find(d => d.value === 0).color
    }

    const quantityAgilePurchase = {
      name: PARTICIPATIONS_COLORS.find(d => d.value === 1).name,
      data: item.quantityAgilePurchaseQuotesEMT,
      dataTotals: item.quantityAgilePurchaseQuotesTotals,
      percentage: item.percentageAgilePurchaseQuotesEMT,
      color: PARTICIPATIONS_COLORS.find(d => d.value === 1).color
    }

    const quantityPurchaseOrders = {
      name: PARTICIPATIONS_COLORS.find(d => d.value === 2).name,
      data: item.quantityPurchaseOrdersEMT,
      dataTotals: item.quantityPurchaseOrdersTotals,
      percentage: item.percentageQuantityOrdersPurchaseEMT,
      color: PARTICIPATIONS_COLORS.find(d => d.value === 2).color
    }

    const quantityParticipating = {
      name: PARTICIPATIONS_COLORS.find(d => d.value === 3).name,
      data: item.quantityEMTParticipating,
      dataTotals: item.quantityTotalParticipatingCompanies,
      percentage: item.percentageEMTParticipating,
      color: PARTICIPATIONS_COLORS.find(d => d.value === 3).color
    }

    return [quantityOffers, quantityAgilePurchase, quantityPurchaseOrders, quantityParticipating]
  }, [data, yearValue])

  const handleViewChange = newView => {
    setActiveView(newView)
    handleChangeView(newView)
  }
  const headerDef = [
    { label: 'Año', value: 'year' },
    { label: '% Ofertas enviadas a Licitaciones por EMT', value: 'percentageOffersEMT' },
    { label: '% Cotizaciones Compra Ágil por EMT', value: 'percentageAgilePurchaseQuotesEMT' },
    { label: '% Órdenes de compra recibidas por EMT', value: 'percentageQuantityOrdersPurchaseEMT' },
    { label: '% Participación EMT', value: 'percentageEMTParticipating' }
  ]
  const recordsDecoratorsDef = [
    d => d.year,
    d => d.percentageOffersEMT,
    d => d.percentageAgilePurchaseQuotesEMT,
    d => d.percentageQuantityOrdersPurchaseEMT,
    d => d.percentageEMTParticipating
  ]

  useEffect(() => {
    if (!endYear) return
    dispatch(onGetSmallerCompaniesParticipationThunk())
  }, [endYear])

  return (
    <Root>
      <Content>
        <Typography
          color="primary"
          fontSize="18px"
          fontWeight="bold"
          lineHeight="21px"
          margin="0 0 24px"
          variant="body1"
        >
          Porcentaje de participación
        </Typography>
        <GridContainer>
          <GridItem xs={6}>
            <Title>Participación de Empresas de menor tamaño</Title>
          </GridItem>
        </GridContainer>
        <GridContainer justifyContent="space-between" spacing={2}>
          <GridItem xs={6}>
            <Description>
              Conoce la información de la participación de proveedores de Empresas de menor tamaño en Mercado Público en
              los últimos años. <Strong>Se entiende por participación </Strong>cuando proveedores envían ofertas o
              cotizaciones o han recibido órdenes de compra.
            </Description>
          </GridItem>
          <GridItem xs={5}>
            <CardCustom color="primary">
              <GridItem xs={1} padding="10px 0 0 0">
                <Info>
                  <InfoCircle>
                    <ExclamationIcon />
                  </InfoCircle>
                </Info>
              </GridItem>
              <GridItem xs={11} padding="10px 4px">
                <GridItem xs={12}>
                  <Typography
                    fontSize="14px"
                    lineHeight="16px"
                    variant="body1"
                    sx={{ color: theme => theme.palette.custom.corduroy }}
                  >
                    El tamaño del proveedor es información provista por el SII. <br />
                    <DialogLink onClick={handleOpenHelp}>Ver información completa</DialogLink>
                  </Typography>
                </GridItem>
              </GridItem>
            </CardCustom>
          </GridItem>
        </GridContainer>

        <DataLayout
          disableCaptureImage={disableCaptureImage}
          disableDownloadFile={disableDownloadFile}
          header={
            <GridContainer alignItems="center" justifyContent="space-between" spacing={1}>
              {activeView === DataLayoutViews.CHART && (
                <GridItem md={3} xs={12}>
                  <Select
                    label="Año"
                    loading={loading}
                    options={yearsOptions}
                    value={yearValue}
                    onChange={handleChangeYear}
                  />
                </GridItem>
              )}
              {activeView === DataLayoutViews.TABLE && (
                <GridItem xs={5}>
                  <InfoLabel margin="0 0 20px 0">
                    Porcentaje de Empresas de Menor tamaño respecto al total de proveedores que participan.
                  </InfoLabel>
                </GridItem>
              )}
            </GridContainer>
          }
          infographic={<InfoSmallerCompaniesParticipation />}
          loading={loading}
          noData={!loading && data?.length === 0}
          noDataText="El organismo comprador no cuenta con información relacionada a sus proveedores durante el año seleccionado."
          ref={dataLayoutRef}
          updatedDate={latestUpdate}
          onCaptureImage={handleCaptureImage}
          onChangeView={handleViewChange}
          onDownloadFile={handleDownloadFile}
          paddingFooter="10px"
        >
          <DataLayoutChild view={DataLayoutViews.CHART}>
            <Card color="blank" elevation="1" margin="0 0 32px">
              <GridContainer padding="0 0 1px">
                <GridItem xs={3.6}>
                  <InfoLabel margin="0 0 40px 0">
                    Porcentaje de Empresas de Menor tamaño respecto al total de proveedores que participan.
                  </InfoLabel>
                </GridItem>
                <GridItem xs={12}>
                  <DonutsChart id="smaller-companies-participation-chart" data={series} />
                </GridItem>
              </GridContainer>
            </Card>
          </DataLayoutChild>

          <DataLayoutChild view={DataLayoutViews.TABLE}>
            <Card color="blank" elevation="1" margin="0 0 32px">
              <GridContainer padding="0 0 20px">
                <GridItem xs={12}>
                  <DataTable
                    headers={headerDef}
                    headersCellsProps={[
                      { enableSort: true, width: '15%' },
                      { enableSort: true },
                      { enableSort: true },
                      { enableSort: true },
                      { enableSort: true }
                    ]}
                    records={data}
                    loading={loading}
                    recordsDecorators={recordsDecoratorsDef}
                  />
                </GridItem>
              </GridContainer>
            </Card>
            <GridContainer justifyContent="flex-end" spacing={1}>
              <GridItem xs={7}>
                <InfoLabel margin="0 0 20px 0">
                  Columna Participación EMT: Entrega el porcentaje de empresas que participan que corresponden a EMT,
                  (participan= recibe OC y/o envía oferta a Licitaciones y/o envía cotizaciones a Compra Ágil).
                </InfoLabel>
              </GridItem>
            </GridContainer>
          </DataLayoutChild>
        </DataLayout>
        <InfoEMTDialog open={helpOpen} onClose={handleCloseHelp} />
      </Content>
    </Root>
  )
}

export default SmallerCompaniesParticipation
